import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  classNames,
  convertToDefEventPara,
  yesOrNoOptions,
} from "../../../shared/utility";
import RequiredFieldLabel from "../../controls/RequiredFieldLabel";

export const TRUCK_INSPECTION_QUESTIONS = [
  {
    name: "truck_question_1",
    label:
      "Truck is clean, no foul odour or condensation present",
  },
  {
    name: "truck_question_2",
    label:
      "Truck has no rust, mold, flaking paint. No damage to floors, walls or ceiling",
  },
  {
    name: "truck_question_3",
    label:
      "Truck doors are sealed properly to prevent entrance of pests, dust and foreign material during transport",
  },
  {
    name: "truck_question_4",
    label: "Product condition is acceptable",
  },
];

function getAnsweredCount(values) {
  return TRUCK_INSPECTION_QUESTIONS.filter((q) => {
    const answer = values[q.name];
    return answer && answer.value;
  }).length;
}

function YesNoRadioGroup({
  name,
  label,
  value,
  error,
  disabled,
  required,
  handleInputChange,
}) {
  const selectedValue = value?.value;

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-gray-900">
        <RequiredFieldLabel required={required}>
          {label}
        </RequiredFieldLabel>
      </legend>
      <div className="flex gap-2">
        {yesOrNoOptions.map((option) => {
          const isSelected = selectedValue === option.value;
          return (
            <label
              key={option.value}
              className={classNames(
                "flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm font-medium transition-colors",
                isSelected
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                disabled ? "cursor-not-allowed opacity-60" : "",
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                disabled={disabled}
                required={required}
                onChange={() =>
                  handleInputChange(convertToDefEventPara(name, option))
                }
                className="sr-only"
              />
              {option.label}
            </label>
          );
        })}
      </div>
      {error ? <p className="text-sm font-medium text-red-500">{error}</p> : null}
    </fieldset>
  );
}

function QuestionList(props) {
  const { values, errors, handleInputChange, disabled, required } = props;

  return (
    <div className="space-y-4">
      {TRUCK_INSPECTION_QUESTIONS.map((question) => (
        <YesNoRadioGroup
          key={question.name}
          name={question.name}
          label={question.label}
          value={values[question.name]}
          error={errors[question.name]}
          disabled={disabled}
          required={required}
          handleInputChange={handleInputChange}
        />
      ))}
    </div>
  );
}

export default function TruckInspectionChecklist(props) {
  const {
    values,
    errors,
    handleInputChange,
    disabled = false,
    collapsible = true,
    defaultOpen = false,
    title = "Receiving inspection",
    embedded = false,
    required = false,
  } = props;

  const answeredCount = getAnsweredCount(values);
  const totalCount = TRUCK_INSPECTION_QUESTIONS.length;
  const isComplete = answeredCount === totalCount;

  if (!collapsible) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {title}
          </h3>
          <span
            className={classNames(
              "text-xs font-medium",
              isComplete ? "text-green-600" : "text-gray-500",
            )}
          >
            {answeredCount}/{totalCount} answered
          </span>
        </div>
        <QuestionList
          values={values}
          errors={errors}
          handleInputChange={handleInputChange}
          disabled={disabled}
          required={required}
        />
      </div>
    );
  }

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div
          className={
            embedded
              ? "overflow-hidden rounded-md border border-gray-200"
              : "card-custom overflow-hidden"
          }
        >
          <Disclosure.Button className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {title}
              </h3>
              <p
                className={classNames(
                  "mt-0.5 text-xs font-medium",
                  isComplete ? "text-green-600" : "text-gray-500",
                )}
              >
                {answeredCount}/{totalCount} answered
              </p>
            </div>
            <ChevronDownIcon
              className={classNames(
                "h-5 w-5 text-gray-500 transition-transform",
                open ? "rotate-180" : "",
              )}
            />
          </Disclosure.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel className="border-t border-gray-200 p-4">
              <QuestionList
                values={values}
                errors={errors}
                handleInputChange={handleInputChange}
                disabled={disabled}
                required={required}
              />
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
}
